import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import _ from 'lodash';
import { trackEvent } from '../Analytics';

import Menu, { MenuList, MenuItem, MenuLink, MenuLinkImage } from './Menu';

import { FauxButton } from '../Boneyard';
import OutsideListener from '../OutsideListener';
import ConditionalWrap from '../ConditionalWrap';

// Only exists so we have somewhere to add an `onMouseLeave` listener
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const FlyoutLink = styled(MenuLink).attrs({
  role: 'menuitem',
})``;

// `button` does not support flex styling
const FlyoutMenuLink = props => (
  <MenuLink {...props} aria-label="Toggle menu" as={FauxButton}>
    {props.icon && <MenuLinkImage src={props.icon} alt="" />}
    {props.children}
  </MenuLink>
);

FlyoutMenuLink.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.string,
};

const _FlyoutToggle = props => {
  const toggle = event => {
    event.preventDefault();
    event.stopPropagation();
    _.isString(props.children) &&
      trackEvent('Site Click Tracking', 'HO Top Nav', props.children);

    if (props.disabled) {
      return;
    }

    props.onToggle(!props.expanded);
  };

  const aria = {
    'aria-haspopup': true,
    'aria-controls': props['aria-controls'],
    'aria-expanded': props.expanded,
  };

  return (
    <FlyoutMenuLink
      {...props}
      {...aria}
      onClick={toggle}
      expanded={props.expanded}
      children={props.children}
    />
  );
};

// Todo: Cannot require 'aria-controls' since it is applied by a parent render call using `cloneElement`
_FlyoutToggle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  /** Useful when waiting for child menu data to be fetched */
  disabled: PropTypes.bool,
  /** Toggle callback containing current toggle state */
  onToggle: PropTypes.func,
  /** Decides if the component is expanded or not */
  expanded: PropTypes.bool,
  'aria-controls': PropTypes.string.isRequired,
};

_FlyoutToggle.defaultProps = {
  disabled: false,
  expanded: false,
  onToggle: Function.prototype,
};

// button aria-controls={menuId} aria-haspopup aria-expanded
const FlyoutToggle = styled(_FlyoutToggle)`
  /* Hide the arrow whilst marked as disabled */
  ${props =>
    !props.disabled &&
    props.withArrow &&
    css`
      /* 5px spacing between text and arrow */
      padding-right: calc(${props => props.theme.spacing(4)} + 5px) !important;

      /* ensure padding explicitly respected */
      padding-bottom: ${props => props.theme.spacing(2)};

      &:after {
        content: '';
        position: absolute;
        right: ${props => props.theme.spacing(2)};
        top: auto;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid;

        ${props.expanded &&
          css`
            /* Flip arrow */
            border-top: none;
            border-bottom: 5px solid;
          `};
      }
    `}

  ${props => props.theme.query.md} {
    ${props =>
      !props.disabled &&
      props.withArrow &&
      css`
        &::after {
          top: auto;

          /* Add border width */
        }
      `};
  }
  ${props => props.theme.query.lg} {
    &::after {
      bottom: 17px;
    }
  }
  /* Persist the sibling sub-menu to be open */
  ${props =>
    !props.disabled &&
    props.expanded &&
    css`
      & + ${FlyoutMenu} {
        display: block;
      }
    `} ${props =>
    props.center &&
    css`
      align-items: center;
    `};
`;

FlyoutToggle.propTypes = {
  /** Vertically center content */
  center: PropTypes.bool,
  /** Displays an arrow pointing up or down based on the expanded state */
  withArrow: PropTypes.bool,
};

FlyoutToggle.defaultProps = {
  center: false,
  withArrow: true,
};

const _FlyoutMenu = props => {
  const [expanded, setExpanded] = useState(false);
  const onToggle = expanded => setExpanded(expanded);
  const closeOnMouseLeave = () => {
    if (props.closeOnMouseLeave) {
      onToggle(false);
    }
  };
  const collapse = () => setExpanded(false);
  const list = React.Children.toArray(props.children);

  const _extract = component =>
    list.filter(child => child.type.displayName === component.displayName);

  const control = _extract(FlyoutToggle)[0];

  const items = _extract(MenuItem).map((item, i) =>
    React.createElement(
      MenuItem,
      Object.assign({}, item.props, {
        key: i,
        role: 'none',
        onClick: collapse,
      })
    )
  );

  const toggle = React.cloneElement(control, {
    ...control.props,
    'aria-controls': props.menuId,
    onToggle: onToggle,
    expanded: expanded,
  });

  return (
    <Wrapper onMouseLeave={closeOnMouseLeave}>
      <ConditionalWrap
        condition={!!props.closeOnClickOutside}
        wrap={children => (
          <OutsideListener handleClickOutside={onToggle}>
            {children}
          </OutsideListener>
        )}
      >
        {toggle}
        <MenuList
          role="menu"
          id={props.menuId}
          aria-hidden={!expanded}
          className={props.className}
          children={items}
        />
      </ConditionalWrap>
    </Wrapper>
  );
};

_FlyoutMenu.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  /** Menu id attribute */
  menuId: PropTypes.string,
  /** Makes the menu close when the cursor is outside of the menu area */
  closeOnMouseLeave: PropTypes.bool,
  closeOnClickOutside: PropTypes.bool,
};

_FlyoutMenu.defaultProps = {
  menuId: _.uniqueId('FlyoutMenu-'),
  closeOnMouseLeave: false,
  closeOnClickOutside: true,
};

const FlyoutMenu = styled(_FlyoutMenu)`
  position: absolute;
  top: 100%;
  ${props => props.align}: 0;
  min-width: calc(50vw);

  /* Hide sub-menu by default */
  display: none;

  ${MenuItem} {
    display: block;
  }
  ${MenuLink} {
    justify-content: ${props =>
      props.align === 'right' ? 'flex-end' : 'flex-start'};
    background-color: ${props => props.theme.colors.hob.topMenu.link};
    color: ${props => props.theme.colors.hob.topMenu.linkBg};
    padding: ${props => props.theme.spacing(2)};
    &:hover,
    &:focus {
      background-color: ${props =>
        props.theme.colors.hob.topMenu.subMenuActive};
    }
  }

  ${FlyoutLink}, ${FlyoutToggle} {
    min-height: 50px;
    border: none;
    justify-content: ${props =>
      props.align === 'right' ? 'flex-end' : 'flex-start'};
    align-items: center;
    padding: 0.625rem;
    background-color: ${props => props.theme.colors.hob.topMenu.link};
    color: ${props => props.theme.colors.hob.topMenu.linkBg};

    &:hover,
    &:focus {
      background-color: ${props =>
        props.theme.colors.hob.topMenu.subMenuActive};
    }
  }

  ${FlyoutToggle} {
    &:after {
      top: calc(50% - 2.5px);
    }
  }

  ${Menu} {
    height: auto;
    flex-direction: column;
  }

  ul {
    position: static;

    ${FlyoutLink} {
      ${props => props.theme.query.xs} {
        padding: 40px;
      }
      background-color: ${props => props.theme.colors.hob.topMenu.link};
      color: ${props => props.theme.colors.hob.topMenu.subMenuSubMenuLink};

      &:hover,
      &:focus {
        background-color: ${props =>
          props.theme.colors.hob.topMenu.subMenuSubMenuActive};
      }
    }
  }

  ${props => props.theme.query.sm} {
    width: 320px;
    min-width: auto;
  }

  ${props => props.theme.query.lg} {
    width: 100%;

    ${FlyoutLink}, ${FlyoutToggle} {
      min-height: 0;
      align-items: flex-start;

      /* Allows text wrapping on IE 11 */
      flex-direction: row;
    }
  }
`;

FlyoutMenu.propTypes = {
  /** Horizontal menu and text alignment */
  align: PropTypes.oneOf(['left', 'right']),
};

FlyoutMenu.defaultProps = {
  align: 'right',
};

export { FlyoutToggle, FlyoutLink };
export default FlyoutMenu;
